import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router';
import { useAtomValue } from 'jotai';
import { AlertCircleIcon, CircleIcon, InfoIcon, X } from 'lucide-react-native';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';
import { z } from 'zod';

import { headerItem } from '@/components/HeaderActions';
import { Header } from '@/components/header';
import { Heading } from '@/components/heading';
import { getScreenColumnClasses } from '@/components/screenColumn';
import { Alert, AlertIcon, AlertText } from '@/components/ui/alert';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { Radio, RadioGroup, RadioIcon, RadioIndicator, RadioLabel } from '@/components/ui/radio';
import { Text } from '@/components/ui/text';
import { Textarea, TextareaInput } from '@/components/ui/textarea';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { FEEDBACK_TYPES } from '@/constants/feedback';
import { ROUTES } from '@/constants/routes';
import { useInquiryClose } from '@/hooks/inbox/useInquiryClose';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { useColors } from '@/hooks/useColors';
import { useToaster } from '@/hooks/useToaster';
import { markInboxFeedbackSubmitted } from '@/services/conversations/cache';
import type { InquiryV2 } from '@/services/getInquiriesV2';
import { type GetInquiryV2ResponseExpert, getInquiryV2QueryOptions } from '@/services/getInquiryV2';
import { userIdAtom } from '@/services/globalState';
import { sendInquiryFeedbackV2 } from '@/services/sendInquiryFeedbackV2';
import { isMedicalOnDemandExpert } from '@/utils/expertHelpers';

const SCREEN_COLUMN = getScreenColumnClasses({ gutter: 'px-5' });

const IMPACT_OPTIONS = [
  { value: '1', label: 'Not at all' },
  { value: '2', label: 'Confirmed' },
  { value: '3', label: 'Minor change' },
  { value: '4', label: 'Major change' },
];

const HELPFUL_OPTIONS = [
  { value: '1', label: 'Not at all - Did not address my question' },
  { value: '2', label: 'Slightly - Offered limited help' },
  { value: '3', label: 'Moderately - Answered part of my question' },
  { value: '4', label: 'Significantly - Fully addressed my questions' },
];

const RATING_OPTIONS = ['1', '2', '3', '4', '5'];

const MOD_RATING_OPTIONS = ['1', '2', '3', '4', '5', '6', '7'];

const EXPERT_CONFIG = {
  additionalFeedbackPlaceholder: 'Please provide us with some details about your selections',
  events: {
    dismissed: ANALYTICS_EVENTS.INQUIRY_FEEDBACK_DISMISSED,
    failed: ANALYTICS_EVENTS.INQUIRY_FEEDBACK_FAILED,
    submitted: ANALYTICS_EVENTS.INQUIRY_FEEDBACK_SUBMITTED,
  },
  feedbackType: FEEDBACK_TYPES.EXPERT,
  footerAlertCopy: null,
  headerAlertCopy: 'Your responses will not be shared with your expert.',
  multipleChoiceOptions: IMPACT_OPTIONS,
  multipleChoiceQuestion: 'How did this inquiry impact your treatment decision?',
  ratingOptions: RATING_OPTIONS,
  ratingQuestion: 'How likely are you to use this expert again?',
  title: 'Inquiry feedback',
};

const MOD_CONFIG = {
  additionalFeedbackPlaceholder:
    'Please share any feedback about your experience of the information provided',
  events: {
    dismissed: ANALYTICS_EVENTS.MOD_INQUIRY_FEEDBACK_DISMISSED,
    failed: ANALYTICS_EVENTS.MOD_INQUIRY_FEEDBACK_FAILED,
    submitted: ANALYTICS_EVENTS.MOD_INQUIRY_FEEDBACK_SUBMITTED,
  },
  feedbackType: FEEDBACK_TYPES.PRODUCT_SUPPORT,
  footerAlertCopy:
    'Your responses will be anonymized and shared in aggregate with the J&J Medical Information Team.',
  headerAlertCopy: null,
  multipleChoiceOptions: HELPFUL_OPTIONS,
  multipleChoiceQuestion:
    'How helpful was the information you received in addressing your question?',
  ratingOptions: MOD_RATING_OPTIONS,
  ratingQuestion:
    'How likely are you to recommend this channel for treatment-related questions to a colleague?',
  title: 'Treatment Support Feedback',
};

type FeedbackProps = {
  isOpen?: boolean;
  onClose?: () => void;
  onSuccess?: (rating?: string) => void;
  conversationId?: string;
};

export default function Feedback(props: FeedbackProps) {
  const userId = useAtomValue(userIdAtom);

  const { id } = useLocalSearchParams<{
    id: string;
  }>();

  const conversationId = props.conversationId || id;

  const { data: inquiry } = useQuery(getInquiryV2QueryOptions({ conversationId }));

  const { state: closeState, completeFeedback } = useInquiryClose();

  const shouldNavigateAfterClose = closeState.navigationTarget?.shouldNavigate === true;

  const expert = inquiry?.recipient;

  const isProductGroupExpert = isMedicalOnDemandExpert(expert);

  const feedbackModalConfig = isProductGroupExpert ? MOD_CONFIG : EXPERT_CONFIG;

  const { replace } = useRouter();

  const { colors } = useColors();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    getValues,
  } = useForm({
    defaultValues: {
      impact: '',
      rating: '',
      feedback: '',
    },
    resolver: zodResolver(
      z.object({
        impact: z.string().optional(),
        rating: z.string().optional(),
        feedback: z.string().optional(),
      })
    ),
  });

  /**
   * Handle feedback dismissal. Sends dismissed status to backend.
   * Called by beforeRemove listener when navigating away.
   */
  const onBack = () => {
    if ((!isSuccess || isWeb) && userId) {
      mutate({
        conversationId,
        inquiryId: inquiry?.id,
        events: feedbackModalConfig.events,
        feedbackStatus: 'Dismissed',
        feedbackType: feedbackModalConfig.feedbackType,
        expertImpact: 0,
        expertRatingPoints: 0,
        additionalFeedback: '',
      });
    }
  };

  /**
   * Handle X button press.
   *
   * For grouped flow: dispatch COMPLETE_FEEDBACK action to transition state
   * to 'complete'. InquiryCloseNavigationEffect responds and handles navigation.
   *
   * For non-grouped flow: use normal back navigation.
   */
  const handleDismiss = () => {
    onBack();

    if (shouldNavigateAfterClose) {
      completeFeedback();
    } else {
      navigateBack();
    }
  };

  const navigation = useNavigation();

  const queryClient = useQueryClient();

  const toast = useToaster();

  const { mutate, isSuccess, isPending } = useMutation({
    mutationFn: sendInquiryFeedbackV2,
    onError: (error, variables) => {
      console.error(`Feedback sendInquiryFeedback:${conversationId} ERROR:`, error);

      if (variables.feedbackStatus === 'Submitted') {
        toast.showError({
          title: 'Unable to submit feedback',
        });
      }
    },
    onSuccess: (_, variables) => {
      if (variables.feedbackStatus !== 'Submitted') {
        return;
      }

      markInboxFeedbackSubmitted(queryClient, userId, conversationId);

      queryClient.setQueryData<InquiryV2>(
        getInquiryV2QueryOptions({ conversationId }).queryKey,
        (prev) => {
          if (!prev) {
            return prev;
          }

          return { ...prev, feedbackStatus: 'Submitted' };
        }
      );

      if (isWeb) {
        props.onSuccess?.(variables.expertRatingPoints?.toString());
      }
    },
  });

  useEffect(() => {
    if (isWeb) {
      return;
    }

    if (isSuccess) {
      navigation.removeListener('beforeRemove', onBack);

      const rating = getValues('rating');

      replace(`/feedback/success${rating ? `?rating=${rating}` : ''}`);
    }

    navigation.addListener('beforeRemove', onBack);

    return () => {
      return navigation.removeListener('beforeRemove', onBack);
    };
  }, [isSuccess]);

  const onSubmit = handleSubmit(async (data) => {
    if (!userId) {
      return;
    }

    mutate({
      conversationId,
      inquiryId: inquiry?.id,
      events: feedbackModalConfig.events,
      feedbackStatus: 'Submitted',
      feedbackType: feedbackModalConfig.feedbackType,
      expertImpact: Number(data.impact),
      expertRatingPoints: Number(data.rating),
      additionalFeedback: data.feedback,
    });
  });

  const { bottom } = useSafeAreaInsets();

  const { navigateBack } = useBackNavigation(ROUTES.INBOX);

  const formContent = (
    <KeyboardAwareScrollView
      mode="layout"
      contentInsetAdjustmentBehavior="automatic"
      bounces={false}
      bottomOffset={18}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="on-drag"
      className="mb-2"
      contentContainerClassName={twJoin('py-5', isWeb && 'px-1.5', SCREEN_COLUMN.content)}>
      <VStack space="2xl">
        {feedbackModalConfig.headerAlertCopy && (
          <Alert action="info" variant="solid">
            <AlertIcon as={InfoIcon} />
            <AlertText size="sm" className="flex-1">
              {feedbackModalConfig.headerAlertCopy}
            </AlertText>
          </Alert>
        )}
        <Controller
          name="impact"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FormControl isInvalid={!!errors.impact?.message}>
              <FormControlLabel className="mb-2">
                <FormControlLabelText>
                  {feedbackModalConfig.multipleChoiceQuestion}
                </FormControlLabelText>
              </FormControlLabel>
              <RadioGroup value={value} onChange={onChange}>
                {feedbackModalConfig.multipleChoiceOptions.map(({ value, label }) => (
                  <Radio value={value} key={value}>
                    <RadioIndicator>
                      <RadioIcon as={CircleIcon} />
                    </RadioIndicator>
                    <RadioLabel>{label}</RadioLabel>
                  </Radio>
                ))}
              </RadioGroup>
              <FormControlError className="mt-2">
                <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                <FormControlErrorText size="xs">{errors.impact?.message}</FormControlErrorText>
              </FormControlError>
            </FormControl>
          )}
        />
        <Controller
          name="rating"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FormControl isInvalid={!!errors.rating?.message} className="gap-y-2">
              <FormControlLabel>
                <FormControlLabelText>{feedbackModalConfig.ratingQuestion}</FormControlLabelText>
              </FormControlLabel>
              <HStack className="gap-x-2 web:gap-x-4" testID="feedback-rating-group">
                {feedbackModalConfig.ratingOptions.map((rating) => (
                  <Button
                    key={rating}
                    testID={`feedback-rating-${rating}`}
                    className="min-w-0 flex-1 px-0"
                    size="sm"
                    variant={rating === value ? 'solid' : 'outline'}
                    onPress={() => onChange(rating)}>
                    <ButtonText>{rating}</ButtonText>
                  </Button>
                ))}
              </HStack>
              <HStack className="justify-between">
                <Text size="xs">Not likely</Text>
                <Text size="xs">Highly likely</Text>
              </HStack>
              <FormControlError>
                <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                <FormControlErrorText size="xs">{errors.rating?.message}</FormControlErrorText>
              </FormControlError>
            </FormControl>
          )}
        />
        <Controller
          name="feedback"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FormControl>
              <FormControlLabel className="mb-2">
                <FormControlLabelText>Additional Feedback</FormControlLabelText>
              </FormControlLabel>
              <Textarea>
                <TextareaInput
                  style={{ color: colors.typography[900] }}
                  maxLength={500}
                  value={value}
                  onChangeText={onChange}
                  placeholder={feedbackModalConfig.additionalFeedbackPlaceholder}
                  textAlignVertical="top"
                />
              </Textarea>
              <FormControlHelper>
                <FormControlHelperText size="sm">
                  Characters remaining: {500 - value.length}
                </FormControlHelperText>
              </FormControlHelper>
            </FormControl>
          )}
        />
        {feedbackModalConfig.footerAlertCopy && (
          <Alert action="info" variant="solid">
            <AlertIcon as={InfoIcon} />
            <AlertText size="sm" className="flex-1">
              {feedbackModalConfig.footerAlertCopy}
            </AlertText>
          </Alert>
        )}
        {!isWeb && (
          <Button
            className={twJoin('mx-5 gap-x-2', !isDirty && 'opacity-50')}
            onPress={onSubmit}
            disabled={isPending}>
            {isPending ? <ButtonSpinner color={colors.primary[200]} /> : null}
            <ButtonText>Submit</ButtonText>
          </Button>
        )}
      </VStack>
    </KeyboardAwareScrollView>
  );

  const feedbackTestId = isProductGroupExpert ? 'feedback-form-mod' : 'feedback-form-standard';

  if (isWeb) {
    return (
      <Modal
        isOpen={props.isOpen}
        onClose={() => {
          onBack();

          props.onClose?.();
        }}>
        <ModalBackdrop />
        <ModalContent testID={feedbackTestId}>
          <ModalHeader>
            <Heading size="h2-md" testID="feedback-title">
              {feedbackModalConfig.title}
            </Heading>
            <ModalCloseButton>
              <Icon as={X} size="md" />
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody className="max-h-[60vh]">{formContent}</ModalBody>
          <ModalFooter className="mt-3">
            <Button
              testID="feedback-submit-button"
              className={twJoin('mx-5 gap-x-2', !isDirty && 'opacity-50')}
              onPress={onSubmit}
              disabled={isPending}>
              {isPending ? <ButtonSpinner color={colors.primary[200]} /> : null}
              <ButtonText>Submit</ButtonText>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <VStack className="mx-auto w-full max-w-screen-md flex-1" style={{ paddingBottom: bottom }}>
      <Header
        headerShown
        contentScrollsUnderHeader
        headerShadowVisible
        coversActingAsBanner
        title={feedbackModalConfig.title}
        leftItems={[headerItem.close(handleDismiss)]}
      />

      <VStack className={twJoin('flex-1', SCREEN_COLUMN.frame)}>{formContent}</VStack>
    </VStack>
  );
}
